import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ProvisionStatus {
  systemId: string;
  isProvisioned: boolean;
  isConflict: boolean;
  warningApplies: boolean; // Derived: provisioned & !conflict
}


@Injectable({ providedIn: 'root' }) // NOTE: For dialog scoping, remove this and add to dialog component providers.
export class DeploymentProvisionStateService {
  private readonly statusMapSubject = new BehaviorSubject<Record<string, ProvisionStatus>>({});
  /** Map of systemId -> ProvisionStatus */
  readonly statusBySystem$: Observable<Record<string, ProvisionStatus>> = this.statusMapSubject.asObservable();

  /** Array of statuses */
  readonly statuses$: Observable<ProvisionStatus[]> = this.statusBySystem$.pipe(
    map(record => Object.values(record))
  );

  /** Systems with conflicts */
  readonly conflicts$: Observable<ProvisionStatus[]> = this.statuses$.pipe(
    map(list => list.filter(s => s.isConflict))
  );

  /** Systems that currently have a warning (provisioned without conflict) */
  readonly warningSystems$: Observable<ProvisionStatus[]> = this.statuses$.pipe(
    map(list => list.filter(s => s.warningApplies))
  );

  /** Update provisioning status for a system. */
  update(status: ProvisionStatus): void {
    const current = { ...this.statusMapSubject.getValue() };
    current[status.systemId] = status;
    this.statusMapSubject.next(current);
  }

  /** Clear provisioning status for a system (e.g., selection cleared). */
  clear(systemId: string): void {
    const current = { ...this.statusMapSubject.getValue() };
    if (current[systemId]) {
      delete current[systemId];
      this.statusMapSubject.next(current);
    }
  }
}
